import React, { useEffect, memo, useCallback } from 'react';
import { Grid, Header, Button, Icon, Message } from 'semantic-ui-react';
import { useSharedData } from '../../hooks/core/useZoneHooks';

/**
 * BooksList-enh component
 * Enhanced version of BooksList with improved UI matching BookListManager-bcw
 * Displays a list of books for a selected literary group
 * @param {Object} props - Component props
 * @param {Function} props.onBookSelect - Callback when a book is selected
 * @param {Function} props.onDeleteBook - Callback when a book delete is requested
 * @returns {JSX.Element} BooksList-enh component
 */
const BooksListEnh = memo(({ onBookSelect, onDeleteBook }) => {
  const sharedData = useSharedData();

  // Extract data from new hook
  const {
    // litGroups,
    books,
    selections,
    loading,
    errors
  } = sharedData;

  // Map to old interface for compatibility
  const booksArray = Array.isArray(books) ? books : [];
  // const litGroupsArray = Array.isArray(litGroups) ? litGroups : [];
  const bookId = selections?.bookId;
  const litGroupId = selections?.litGroupId;
  const booksLoading = loading?.books ? 'loading' : 'idle';
  const booksError = errors?.books;
  // const selectedLitGroup = litGroupsArray.find(lg => lg.uuid === litGroupId) || null;

  // Filter books for the selected literary group
  const litGroupBooks = litGroupId
    ? booksArray.filter(book =>
      book.litGroupId === litGroupId || book.literaryGroupId === litGroupId
    )
    : [];

  // Get functions from shared data
  const fetchBooks = sharedData.actions.fetchBooks;
  const setBookSelection = sharedData.actions.setBookSelection;

  // Fetch books when litGroupId changes
  useEffect(() => {
    if (litGroupId) {
      console.log('BooksListEnh fetch litGroupId ', litGroupId);

      fetchBooks(litGroupId);
    }
  }, [fetchBooks, litGroupId]);

  // Handle book selection
  const handleBookSelect = useCallback((book) => {
    setBookSelection(book.uuid);
    if (onBookSelect) {
      onBookSelect(book);
    }
  }, [setBookSelection, onBookSelect]);

  // Handle delete book
  const handleDeleteBook = useCallback((e, book) => {
    e.stopPropagation(); // Prevent row selection
    console.log('Delete book:', book.name);
    // In the future, this will trigger a delete confirmation modal
  }, []);

  // Handle new book
  const handleNewBook = useCallback(() => {
    setBookSelection(null);
    // In the future, this will show the new book form
  }, [setBookSelection]);

  return (
    <div className='bw-books-list-container'>
      <Header className="bw-hdr-with-prompt" as="h3">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          height: '100%'
        }}>
          <span>Books</span>
          <div style={{ width: "160px" }}>
            {booksLoading === 'loading' ?
              <div className="bw-book-message" style={{
                backgroundColor: '#F8FFFF',
                border: 'solid 1px #a9d5de',
                marginLeft: '10px'
              }}>
                <p>Loading books...</p>
              </div>
              : ""}
          </div>
          <Button
            primary
            icon
            labelPosition="left"
            onClick={handleNewBook}
            disabled={!litGroupId}
            style={{ marginTop: '3px', alignItems: 'right' }}
          >
            <Icon name="add" />
            New Book
          </Button>
        </div>
      </Header>
      {/* Books list */}
      {booksLoading === 'failed' ? (
        <Message negative>
          <Message.Header>Error Loading Books</Message.Header>
          <p>{booksError || 'An unknown error occurred'}</p>
        </Message>
      ) : !litGroupId ? (
        <div className="bw-empty-selection-message">
          <Message color='blue'>Select a Literary Group to view its books.</Message>
        </div>
      ) : litGroupBooks && litGroupBooks.length > 0 ? (
        // Scrolling is the stylesheet's job (.bw-books-list). This used to carry
        // style={{ overflow: "none" }} — not a valid value, so it was dropped at parse time and
        // never did anything.
        <div className="bw-books-list ui segment">
          {/* Header row */}
          <Grid>
            <Grid.Row className="bw-list-header-row">
              <Grid.Column width={14}>Title</Grid.Column>
              <Grid.Column width={2}></Grid.Column>
            </Grid.Row>

            {/* Book rows */}
            {litGroupBooks.map(book => {
              return (
                <Grid.Row
                  key={book.uuid}
                  className="bw-list-item-row"
                  style={{
                    backgroundColor: bookId === book.uuid ? '#ebf3fb' : 'transparent'
                  }}
                  onClick={() => handleBookSelect(book)}
                >
                  <Grid.Column width={14}>
                    {book.profileMissing
                      ? `${book.name || 'Untitled Book'} - No Profile`
                      : (book.name || 'Untitled Book')}
                  </Grid.Column>
                  <Grid.Column width={2} textAlign="right">
                    <Icon
                      name="trash alternate"
                      color="red"
                      className="bw-trash-icon"
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => handleDeleteBook(e, book)}
                    />
                  </Grid.Column>
                </Grid.Row>
              )
            })}
          </Grid>
        </div>
      ) : (
        <div className="bw-empty-list-message">
          <p>No books found for this literary group.</p>
        </div>
      )}


    </div>
  );
});

export default BooksListEnh;
